'use client'

import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { useMultiChainPath } from '@/state/info/hooks'
import { PoolData } from '@/views/V3Info/types'

interface PoolTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
}

export function PoolTable<TData, TValue>({ columns, data }: PoolTableProps<TData, TValue>) {
  const router = useRouter()
  const chainPath = useMultiChainPath()
  const [sorting, setSorting] = useState<SortingState>([
    {
      desc: true,
      id: 'volume24h',
    },
  ])
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    sortDescFirst: true,
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
    },
  })

  return (
    <div className={'border rounded-2xl overflow-hidden pb-4'}>
      <Table>
        <TableHeader className={'bg-secondary text-muted-foreground'}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id} className={'text-muted-foreground'}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, rowIndex) => (
              <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className={'cursor-pointer'}>
                {row.getVisibleCells().map((cell, index) => {
                  if (index === 0) {
                    return (
                      <TableCell key={cell.id} className="w-[60px]">
                        {rowIndex + 1}
                      </TableCell>
                    )
                  }

                  return (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  )
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
